import {
  Input,
  Typography,
  Button,
  Controller,
  Delimiter,
  TextEditor,
  useForm,
  BottomNavigation,
  FormDirtyModal,
  PageModal,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import {
  Actions,
  AppArea,
  CreatePermissionDocument,
  PermissionType,
  Subjects,
  TagEntityType,
  UpdatePermissionDocument,
} from 'src/graphql/generated/graphql';
import 'react-quill/dist/quill.snow.css';

import styles from './index.module.css';
import { DocumentNode, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { TagsField } from 'src/components/common/form/fields/tags';
import { PermissionListItem } from 'src/components/permissions/list/types';
import { ActionField } from './fields/action';
import { SubjectField } from './fields/subject';
import { AppAreaField } from './fields/app-area';

type Form = {
  name: string;
  appArea: AppArea;
  subject: Subjects;
  action: Actions;
  condition?: string;
  description: string;
  tagIds?: string[];
};

interface FormProps {
  onFinish?: () => void;
  onClose?: () => void;
  permission?: PermissionListItem;
  refetchQueries?: DocumentNode[];
  title?: React.ReactNode | string;
}

const getFormData = (entity?: PermissionListItem) => ({
  name: entity?.name || '',
  description: entity?.description || undefined,
  appArea: entity?.appArea || AppArea.Common,
  subject: entity?.subject || Subjects.Common,
  action: entity?.action || Actions.Read,
  condition: entity?.condition || '',
  tagIds: entity?.tags?.map((tag) => tag.id) || [],
});

export const PermissionForm = ({
  onFinish,
  permission,
  refetchQueries,
  onClose,
  title,
}: FormProps) => {
  const { t } = useTranslation();
  const [disabledFields, setDisabledFields] = useState<string[]>([]);

  const [createPermission, { loading: loadingCreate }] = useMutation(
    CreatePermissionDocument,
    {
      refetchQueries,
    },
  );

  const [updatePermission, { loading: loadingUpdate }] = useMutation(
    UpdatePermissionDocument,
    {
      refetchQueries,
    },
  );

  const loading = loadingCreate || loadingUpdate;

  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
  } = useForm<Form>({
    defaultValues: getFormData(permission),
  });

  const [appArea, setAppArea] = useState<AppArea>(getValues('appArea'));
  const [subject, setSubject] = useState<Subjects>(getValues('subject'));

  useEffect(() => {
    reset(getFormData(permission));

    const isSystemPermission = permission?.type === PermissionType.System;
    if (isSystemPermission) {
      setDisabledFields(['name', 'action', 'subject', 'appArea', 'condition']);
    }
  }, [permission, reset]);

  const onCancel = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onClose?.();
    }
  };

  const onSubmit = async (form: Form) => {
    if (permission?.id) {
      await updatePermission({
        variables: {
          id: permission.id,
          data: form,
        },
      });
    } else {
      await createPermission({
        variables: {
          data: form as any,
        },
      });
    }

    onFinish?.();
  };

  const titleElement =
    title || (t('permissions.form.create-permission') as string);

  return (
    <PageModal open onClose={onCancel} title={titleElement}>
      <div className={styles.wrapper}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.fieldsWrapper}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.name ? 'red' : ''}
                  >
                    {t('permissions.form.name')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <Input
                    placeholder={t('permissions.form.name')}
                    error={!!errors.name}
                    {...field}
                    ref={undefined}
                    inputRef={field.ref}
                    value={undefined}
                    defaultValue={field.value}
                    disabled={disabledFields?.includes('name')}
                  />
                </div>
              )}
            />
            <Controller
              name="appArea"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('permissions.form.app-area')}
                  </Typography>
                  <AppAreaField
                    hasError={!!errors.appArea}
                    field={field}
                    disabled={disabledFields?.includes('appArea')}
                    onChange={(event) =>
                      setAppArea(event.target.value as AppArea)
                    }
                  />
                </div>
              )}
            />
            <Controller
              name="subject"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('permissions.form.subject')}
                  </Typography>
                  <SubjectField
                    hasError={!!errors.subject}
                    field={field}
                    disabled={disabledFields?.includes('subject')}
                    appArea={appArea}
                    onChange={(event) =>
                      setSubject(event.target.value as Subjects)
                    }
                  />
                </div>
              )}
            />
            <Controller
              name="action"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('permissions.form.action')}
                  </Typography>
                  <ActionField
                    hasError={!!errors.action}
                    field={field}
                    disabled={disabledFields?.includes('action')}
                    subject={subject}
                  />
                </div>
              )}
            />
            <Controller
              name="condition"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <Input
                  label={t('permissions.form.condition')}
                  placeholder={t('permissions.form.condition')}
                  error={!!errors.condition}
                  {...field}
                  ref={undefined}
                  inputRef={field.ref}
                  value={undefined}
                  defaultValue={field.value}
                  disabled={disabledFields?.includes('condition')}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              // required by gql
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.description ? 'red' : ''}
                  >
                    {t('permissions.form.description')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <TextEditor
                    {...field}
                    defaultValue={field.value}
                    disabled={disabledFields?.includes('description')}
                  />{' '}
                </div>
              )}
            />
            <Controller
              name="tagIds"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('common.tags')}
                  </Typography>
                  <TagsField
                    field={field}
                    entityType={TagEntityType.Permission}
                  />
                </div>
              )}
            />
          </div>

          <Delimiter className="mt-6" />

          <BottomNavigation>
            <Button
              className="mr-2"
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              disabled={loading}
            >
              {t('permissions.form.submit-button')}
            </Button>
            <Button variant="outlined" color="primary" onClick={onCancel}>
              {t('common.close')}
            </Button>
          </BottomNavigation>
        </form>
      </div>

      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={() => onClose?.()}
      />
    </PageModal>
  );
};
